/**
 * The comparable rate under each package, in ONE unit for the whole list.
 *
 * It was always coins per hour, rounded to a whole coin: a 30-day boost for 300
 * coins is 0.42 an hour and printed "0 coins/hr" — a price tag saying free — and
 * the week printed "1 coins/hr". When every package lasts at least a day the
 * rate is per day; one sub-day package puts the whole list back on hours, so
 * the numbers stay comparable with each other. One decimal below 10, because
 * that is where rounding changes which package looks cheaper, and two
 * significant digits below 1.
 */
export function boostRate(
  coins: number,
  minutes: number,
  unit: "day" | "hour",
  locale?: string,
): { value: number; text: string } | null {
  if (!Number.isFinite(minutes) || minutes <= 0 || !Number.isFinite(coins) || coins < 0)
    return null;
  const raw = (coins / minutes) * (unit === "day" ? 1440 : 60);
  // Below 1, two significant digits, so a cheap long package never rounds to 0.
  const value =
    raw >= 10 ? Math.round(raw) : raw >= 1 ? Math.round(raw * 10) / 10 : Number(raw.toPrecision(2));
  return { value, text: new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(value) };
}

export function rateUnitFor(minutes: number[]): "day" | "hour" {
  const usable = minutes.filter((m) => Number.isFinite(m) && m > 0);
  return usable.length > 0 && usable.every((m) => m >= 1440) ? "day" : "hour";
}

/**
 * "1 day", "7 days", "3 hours" from a package's minutes. The DB label is
 * catalogue text an admin types (it was German for everyone once), so the
 * duration is built from `minutes`; the label is only the fallback for a row
 * without a usable one. Shared by the boost page and the boost-price note so
 * both name a package the same way.
 */
export function boostDurationLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  minutes: number,
  fallback = "",
): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return fallback;
  if (minutes % 1440 === 0) return t("profile.boost.durationDays", { count: minutes / 1440 });
  if (minutes % 60 === 0) return t("profile.boost.durationHours", { count: minutes / 60 });
  return t("profile.boost.durationMin", { count: minutes });
}
